/**
 * Photo records are retained by the API as Face Search action origins. In the
 * viewer they belong to their source's gallery, not to separate graph nodes.
 * Actual searches originating from a photo remain visible under that source.
 */
export function foldCollectedPhotoNodes(nodes: any[], edges: any[]): { nodes: any[]; edges: any[] } {
  const byId = new Map(nodes.filter((node) => node?.id).map((node) => [node.id, node]));
  const endpoint = (value: any): string => String(value?.id ?? value ?? '');
  const photoIds = new Set<string>(nodes.filter((node) => node?.type === 'face_image').map((node) => node.id));
  edges.forEach((edge) => {
    const child = byId.get(endpoint(edge.target));
    if (edge.type === 'has_face_image' && child && !['image_query', 'face_result'].includes(child.type)) photoIds.add(child.id);
  });
  const parentIds = new Map<string, string>();
  photoIds.forEach((id) => {
    const incoming = edges.find((edge) => endpoint(edge.target) === id);
    parentIds.set(id, String(byId.get(id)?.metadata?.parentNodeId || endpoint(incoming?.source)));
  });
  const owners = new Map<string, string>();
  photoIds.forEach((id) => {
    const visited = new Set([id]);
    let parent = parentIds.get(id);
    while (parent && photoIds.has(parent) && !visited.has(parent)) {
      visited.add(parent);
      parent = parentIds.get(parent);
    }
    if (parent && byId.has(parent) && !photoIds.has(parent)) owners.set(id, parent);
  });
  if (!owners.size) return { nodes, edges };

  const photosByOwner = new Map<string, Array<{ url: string; label: string; sourceNodeId: string }>>();
  owners.forEach((ownerId, photoId) => {
    const photo = byId.get(photoId);
    const meta = photo.metadata || {};
    const urls = [photo.imageUrl, photo.image_url, meta.canonicalUrl, meta.imageUrl,
      meta.queryType === 'image' ? meta.queryValue : null,
      ...(meta.searchableValues?.image || []), ...(meta.searchable?.image || [])];
    const collected = photosByOwner.get(ownerId) || [];
    urls.forEach((value) => {
      const url = typeof value === 'string' ? value.trim() : '';
      if (!/^(https?:\/\/|data:image\/|\/api\/osint\/image\/)/i.test(url) || collected.some((item) => item.url === url)) return;
      collected.push({ url, label: photo.label || 'Photo', sourceNodeId: photoId });
    });
    photosByOwner.set(ownerId, collected);
  });
  const visibleNodes = nodes.filter((node) => !owners.has(node.id)).map((node) => {
    const parentId = owners.get(String(node.metadata?.parentNodeId || ''));
    const photos = photosByOwner.get(node.id);
    if (!photos && !parentId) return node;
    const metadata = { ...(node.metadata || {}), ...(parentId ? { parentNodeId: parentId } : {}) };
    if (photos) {
      metadata.collectedPhotos = [...(metadata.collectedPhotos || []), ...photos];
      const imageUrls = photos.map((photo) => photo.url);
      for (const key of ['searchable', 'searchableValues']) {
        metadata[key] = { ...(metadata[key] || {}), image: [...new Set([...(metadata[key]?.image || []), ...imageUrls])] };
      }
    }
    return { ...node, metadata, imageUrl: node.imageUrl || photos?.[0]?.url };
  });
  const visibleEdges = edges.filter((edge) => !owners.has(endpoint(edge.target))).map((edge) => {
    const owner = owners.get(endpoint(edge.source));
    return owner ? { ...edge, source: owner } : edge;
  });
  return { nodes: visibleNodes, edges: visibleEdges };
}
